"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Eye, Droplets } from "lucide-react";
import type { WaterQualityLeaderboardEntry } from "@/lib/conditions/satellite-water-quality";

function visibilityBadge(secchi: number | null): { label: string; color: string } {
  if (secchi === null) return { label: "No data", color: "bg-slate-100 text-slate-600" };
  if (secchi >= 15) return { label: "Crystal clear", color: "bg-emerald-100 text-emerald-800" };
  if (secchi >= 8) return { label: "Good", color: "bg-teal-100 text-teal-800" };
  if (secchi >= 3) return { label: "Moderate", color: "bg-amber-100 text-amber-800" };
  return { label: "Reduced", color: "bg-red-100 text-red-800" };
}

function cleanlinessBadge(score: number | null): { label: string; color: string } {
  if (score === null) return { label: "No data", color: "bg-slate-100 text-slate-600" };
  if (score <= 15) return { label: "Pristine", color: "bg-emerald-100 text-emerald-800" };
  if (score <= 30) return { label: "Clean", color: "bg-teal-100 text-teal-800" };
  if (score <= 50) return { label: "Moderate", color: "bg-amber-100 text-amber-800" };
  if (score <= 70) return { label: "Elevated", color: "bg-orange-100 text-orange-800" };
  return { label: "Poor", color: "bg-red-100 text-red-800" };
}

function formatNum(val: number | null, dp: number = 1): string {
  if (val === null) return "-";
  return val.toFixed(dp);
}

type ViewMode = "visibility" | "cleanliness";
type VisibilitySortKey = "secchi" | "chlorophyll" | "spm" | "sst";
type CleanlinessSortKey = "score" | "chlorophyll" | "o2" | "nitrate" | "phosphate" | "spm";
type SortKey = VisibilitySortKey | CleanlinessSortKey;

const VIS_KEY_MAP: Record<VisibilitySortKey, keyof WaterQualityLeaderboardEntry> = {
  secchi: "secchiDepthM",
  chlorophyll: "chlorophyllMgM3",
  spm: "spmGM3",
  sst: "sstC",
};

const CLEAN_KEY_MAP: Record<CleanlinessSortKey, keyof WaterQualityLeaderboardEntry> = {
  score: "cleanlinessScore",
  chlorophyll: "chlorophyllMgM3",
  o2: "dissolvedOxygenMlL",
  nitrate: "nitrateMmolM3",
  phosphate: "phosphateMmolM3",
  spm: "spmGM3",
};

export function WaterQualityTable({
  entries,
  countries,
  defaultView = "visibility",
}: {
  entries: WaterQualityLeaderboardEntry[];
  countries: string[];
  defaultView?: ViewMode;
}) {
  const router = useRouter();
  const view = defaultView;

  const [countryFilter, setCountryFilter] = React.useState("all");
  const [sortKey, setSortKey] = React.useState<SortKey>(
    view === "cleanliness" ? "score" : "secchi"
  );
  const [sortAsc, setSortAsc] = React.useState(
    view === "cleanliness"
  );

  function setView(v: ViewMode) {
    if (v === "cleanliness") {
      router.push("/conditions/water-cleanliness");
    } else {
      router.push("/conditions/water-visibility");
    }
  }

  const keyMap = view === "visibility" ? VIS_KEY_MAP : CLEAN_KEY_MAP;

  const filtered = entries
    .filter((e) => countryFilter === "all" || e.country === countryFilter)
    .sort((a, b) => {
      const field = keyMap[sortKey as keyof typeof keyMap];
      const aVal = (a[field] as number | null) ?? -999;
      const bVal = (b[field] as number | null) ?? -999;
      const diff = aVal - bVal;
      return sortAsc ? diff : -diff;
    });

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      if (view === "cleanliness") {
        setSortAsc(key === "score" || key === "spm" || key === "chlorophyll" || key === "nitrate" || key === "phosphate");
      } else {
        setSortAsc(key !== "secchi");
      }
    }
  }

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortAsc ? " ↑" : " ↓") : "";

  const thClass = "px-4 py-3 font-semibold text-text-muted text-[11px] uppercase tracking-[1px]";
  const thSort = `${thClass} cursor-pointer hover:text-navy select-none`;

  return (
    <div>
      {/* View toggle */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex rounded-[9px] bg-warm-bg p-[3px]" role="tablist">
          <button
            role="tab"
            aria-selected={view === "visibility"}
            onClick={() => setView("visibility")}
            className={`flex items-center gap-1.5 px-5 py-2 rounded-[6px] text-[14px] transition-all ${
              view === "visibility"
                ? "bg-white font-semibold text-navy shadow-[0_1px_3px_rgba(0,0,0,0.08)] border border-border-light"
                : "text-text-muted font-medium hover:text-navy"
            }`}
          >
            <Eye className="size-[15px]" />
            Visibility
          </button>
          <button
            role="tab"
            aria-selected={view === "cleanliness"}
            onClick={() => setView("cleanliness")}
            className={`flex items-center gap-1.5 px-5 py-2 rounded-[6px] text-[14px] transition-all ${
              view === "cleanliness"
                ? "bg-white font-semibold text-navy shadow-[0_1px_3px_rgba(0,0,0,0.08)] border border-border-light"
                : "text-text-muted font-medium hover:text-navy"
            }`}
          >
            <Droplets className="size-[15px]" />
            Cleanliness
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <select
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
          className="text-[13px] px-3 py-2 rounded-[10px] border border-border-light bg-white text-navy font-medium"
          aria-label="Filter by country"
        >
          <option value="all">All countries</option>
          {countries.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <span className="text-[12px] text-text-muted self-center ml-auto">
          {filtered.length} spot{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto rounded-[16px] border border-border-light bg-white">
        <table className="w-full text-left text-[13px]">
          <thead className="bg-warm-bg sticky top-0 z-10">
            {view === "visibility" ? (
              <tr>
                <th className={`${thClass} w-8`}>#</th>
                <th className={thClass}>Location</th>
                <th className={thClass}>Country</th>
                <th className={thSort} onClick={() => handleSort("secchi")}>
                  Visibility{sortIndicator("secchi")}
                </th>
                <th className={thClass}>Rating</th>
                <th className={thSort} onClick={() => handleSort("chlorophyll")}>
                  Chlorophyll{sortIndicator("chlorophyll")}
                </th>
                <th className={thSort} onClick={() => handleSort("spm")}>
                  Turbidity{sortIndicator("spm")}
                </th>
                <th className={thSort} onClick={() => handleSort("sst")}>
                  SST{sortIndicator("sst")}
                </th>
              </tr>
            ) : (
              <tr>
                <th className={`${thClass} w-8`}>#</th>
                <th className={thClass}>Location</th>
                <th className={thClass}>Country</th>
                <th className={thSort} onClick={() => handleSort("score")}>
                  Score{sortIndicator("score")}
                </th>
                <th className={thClass}>Rating</th>
                <th className={thSort} onClick={() => handleSort("chlorophyll")}>
                  Chlorophyll{sortIndicator("chlorophyll")}
                </th>
                <th className={thSort} onClick={() => handleSort("o2")}>
                  Dissolved O&#x2082;{sortIndicator("o2")}
                </th>
                <th className={thSort} onClick={() => handleSort("nitrate")}>
                  Nitrate{sortIndicator("nitrate")}
                </th>
                <th className={thSort} onClick={() => handleSort("phosphate")}>
                  Phosphate{sortIndicator("phosphate")}
                </th>
                <th className={thSort} onClick={() => handleSort("spm")}>
                  SPM{sortIndicator("spm")}
                </th>
              </tr>
            )}
          </thead>
          <tbody className="divide-y divide-border-light">
            {filtered.map((entry, i) => {
              if (view === "visibility") {
                const badge = visibilityBadge(entry.secchiDepthM);
                return (
                  <tr key={entry.slug} className="hover:bg-warm-bg/50 transition-colors">
                    <td className="px-4 py-3.5 tabular-nums text-text-muted font-medium">{i + 1}</td>
                    <td className="px-4 py-3.5">
                      <Link
                        href={`/conditions/${entry.slug}`}
                        className="font-semibold text-navy no-underline hover:text-gold transition-colors"
                      >
                        {entry.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3.5 text-text-muted">{entry.country}</td>
                    <td className="px-4 py-3.5 tabular-nums font-bold text-navy">
                      {entry.secchiDepthM !== null ? `${entry.secchiDepthM.toFixed(1)}m` : "-"}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${badge.color}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 tabular-nums text-text-body">
                      {formatNum(entry.chlorophyllMgM3)} <span className="text-[10px] text-text-muted">mg/m3</span>
                    </td>
                    <td className="px-4 py-3.5 tabular-nums text-text-body">
                      {formatNum(entry.spmGM3)} <span className="text-[10px] text-text-muted">g/m3</span>
                    </td>
                    <td className="px-4 py-3.5 tabular-nums text-text-body">
                      {entry.sstC !== null ? `${entry.sstC.toFixed(1)}°C` : "-"}
                    </td>
                  </tr>
                );
              }

              const badge = cleanlinessBadge(entry.cleanlinessScore);
              return (
                <tr key={entry.slug} className="hover:bg-warm-bg/50 transition-colors">
                  <td className="px-4 py-3.5 tabular-nums text-text-muted font-medium">{i + 1}</td>
                  <td className="px-4 py-3.5">
                    <Link
                      href={`/conditions/${entry.slug}`}
                      className="font-semibold text-navy no-underline hover:text-gold transition-colors"
                    >
                      {entry.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3.5 text-text-muted">{entry.country}</td>
                  <td className="px-4 py-3.5 tabular-nums font-bold text-navy">
                    {entry.cleanlinessScore !== null ? entry.cleanlinessScore : "-"}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${badge.color}`}>
                      {badge.label}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 tabular-nums text-text-body">
                    {formatNum(entry.chlorophyllMgM3)} <span className="text-[10px] text-text-muted">mg/m3</span>
                  </td>
                  <td className="px-4 py-3.5 tabular-nums text-text-body">
                    {formatNum(entry.dissolvedOxygenMlL, 2)} <span className="text-[10px] text-text-muted">ml/L</span>
                  </td>
                  <td className="px-4 py-3.5 tabular-nums text-text-body">
                    {formatNum(entry.nitrateMmolM3, 2)} <span className="text-[10px] text-text-muted">mmol/m3</span>
                  </td>
                  <td className="px-4 py-3.5 tabular-nums text-text-body">
                    {formatNum(entry.phosphateMmolM3, 3)} <span className="text-[10px] text-text-muted">mmol/m3</span>
                  </td>
                  <td className="px-4 py-3.5 tabular-nums text-text-body">
                    {formatNum(entry.spmGM3)} <span className="text-[10px] text-text-muted">g/m3</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {filtered.map((entry, i) => {
          if (view === "visibility") {
            const badge = visibilityBadge(entry.secchiDepthM);
            return (
              <Link
                key={entry.slug}
                href={`/conditions/${entry.slug}`}
                className="block bg-white rounded-[14px] border border-border-light p-4 no-underline hover:border-teal-200 hover:shadow-[0_4px_16px_rgba(13,148,136,0.06)] transition-all"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="text-[11px] text-text-muted font-medium tabular-nums mr-2">#{i + 1}</span>
                    <span className="font-semibold text-navy text-[15px]">{entry.name}</span>
                    <div className="text-[12px] text-text-muted mt-0.5">{entry.country}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[18px] font-bold text-navy tabular-nums">
                      {entry.secchiDepthM !== null ? `${entry.secchiDepthM.toFixed(1)}m` : "-"}
                    </div>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.color}`}>
                      {badge.label}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-border-light/60">
                  <div>
                    <div className="text-[10px] text-text-muted uppercase tracking-[0.5px]">Chlorophyll</div>
                    <div className="text-[13px] font-semibold text-navy tabular-nums">{formatNum(entry.chlorophyllMgM3)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-text-muted uppercase tracking-[0.5px]">Turbidity</div>
                    <div className="text-[13px] font-semibold text-navy tabular-nums">{formatNum(entry.spmGM3)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-text-muted uppercase tracking-[0.5px]">SST</div>
                    <div className="text-[13px] font-semibold text-navy tabular-nums">
                      {entry.sstC !== null ? `${entry.sstC.toFixed(1)}°C` : "-"}
                    </div>
                  </div>
                </div>
              </Link>
            );
          }

          const badge = cleanlinessBadge(entry.cleanlinessScore);
          return (
            <Link
              key={entry.slug}
              href={`/conditions/${entry.slug}`}
              className="block bg-white rounded-[14px] border border-border-light p-4 no-underline hover:border-teal-200 hover:shadow-[0_4px_16px_rgba(13,148,136,0.06)] transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="text-[11px] text-text-muted font-medium tabular-nums mr-2">#{i + 1}</span>
                  <span className="font-semibold text-navy text-[15px]">{entry.name}</span>
                  <div className="text-[12px] text-text-muted mt-0.5">{entry.country}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[18px] font-bold text-navy tabular-nums">
                    {entry.cleanlinessScore !== null ? entry.cleanlinessScore : "-"}
                  </div>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.color}`}>
                    {badge.label}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-border-light/60">
                <div>
                  <div className="text-[10px] text-text-muted uppercase tracking-[0.5px]">Chlorophyll</div>
                  <div className="text-[13px] font-semibold text-navy tabular-nums">{formatNum(entry.chlorophyllMgM3)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-text-muted uppercase tracking-[0.5px]">Dissolved O&#x2082;</div>
                  <div className="text-[13px] font-semibold text-navy tabular-nums">{formatNum(entry.dissolvedOxygenMlL, 2)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-text-muted uppercase tracking-[0.5px]">Nitrate</div>
                  <div className="text-[13px] font-semibold text-navy tabular-nums">{formatNum(entry.nitrateMmolM3, 2)}</div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
